// Display helpers shared by the asset screens.

export function formatMoney(value, { cents = false } = {}) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
}

// "2024-04-01" -> "Apr 2024" (date-only strings are parsed as local dates so
// they don't slip a day in US timezones).
export function formatMonthYear(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d || 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

export function formatShortDate(isoDateTime) {
  if (!isoDateTime) return null;
  return new Date(isoDateTime).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function isWarrantyExpired(isoDate) {
  if (!isoDate) return false;
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d) < new Date(new Date().toDateString());
}

// "4.5 yrs" since install, or null.
export function ageSince(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-").map(Number);
  const years = (Date.now() - new Date(y, m - 1, d).getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years < 0) return null;
  if (years < 1) return `${Math.max(1, Math.round(years * 12))} mo`;
  return `${years.toFixed(1).replace(/\.0$/, "")} yrs`;
}

// Asset-form auto-fill. Fills ``fields`` of ``base`` from ``known`` (a
// picked name's or model's usual details; null when the value isn't known).
// ``autofilled`` maps field -> { value, source } for values earlier picks
// filled in. Typed values are never changed. Otherwise:
// - empty fields take the known value;
// - values this ``source`` filled in take the known value, or clear when the
//   new value isn't known (it no longer applies);
// - values the other source filled in give way to a known pick's value, but
//   stay when this value isn't known (renaming doesn't undo a model).
// Returns { next, autofilled }.
export function applyAutofill(base, known, fields, source, autofilled) {
  const next = { ...base };
  const filled = { ...autofilled };
  fields.forEach((key) => {
    const current = String(base[key] ?? "");
    const prior = autofilled[key];
    const stillAutofilled = prior !== undefined && current === prior.value;
    const fresh = known?.[key] ? String(known[key]) : "";
    if (!current.trim() || (stillAutofilled && prior.source === source)) {
      next[key] = fresh || (stillAutofilled ? "" : current);
      if (fresh) filled[key] = { value: fresh, source };
      else delete filled[key];
    } else if (stillAutofilled && fresh) {
      next[key] = fresh;
      filled[key] = { value: fresh, source };
    }
  });
  return { next, autofilled: filled };
}
